/**
 * FPR (FP Race) Round Settings Form Component
 * 
 * Provides the settings form for creating/editing FP Race rounds.
 * Shows flashpoint selection rows inline with segmented difficulty buttons.
 */

import { useState, useCallback, memo } from 'react';
import {
  FprRoundSettings,
  FlashpointSelection,
  FlashpointDifficulty,
  GameType,
  Player,
  RoundPlayer,
  type PlatformFlashpointOption,
} from '@swtorbit/shared-socket-events';
import { AlertModal } from '../../ui/ConfirmModal';
import { RoundFormBase } from '../round';
import { useFlashpointCatalogue } from '../../hooks/useFlashpointCatalogue';
import { Segmented } from '@swtorbit/ui/react';

interface FprRoundSettingsFormProps {
  /** Available players to select from */
  players: Player[];
  /** Callback when form is submitted */
  onSubmit: (settings: FprRoundSettings, players: RoundPlayer[]) => void;
  /** Callback when form is cancelled */
  onCancel: () => void;
  /** Initial values for editing (optional) */
  initialSettings?: FprRoundSettings;
  /** Whether this is an edit form */
  isEditing?: boolean;
  /** All flashpoint selections across all rounds in this game (to grey out) */
  allGameFlashpointSelections?: FlashpointSelection[];
  /** Number of teams currently in the game — caps finalist count */
  teamCount?: number;
}

// In-memory storage for the user's last selected difficulty (persists during session)
let lastSelectedDifficulty: FlashpointDifficulty = 'MM';

/** Flashpoint row component for selecting a flashpoint and its difficulty */
interface FlashpointRowProps {
  index: number;
  selection: FlashpointSelection;
  /** Every pickable flashpoint (platform catalogue) */
  flashpoints: PlatformFlashpointOption[];
  disabledFlashpointIds: Set<string>;
  onUpdate: (index: number, selection: FlashpointSelection) => void;
  onRemove: (index: number) => void;
  isOnlyRow: boolean;
}

const FlashpointRow = memo(function FlashpointRow({
  index,
  selection,
  flashpoints,
  disabledFlashpointIds,
  onUpdate,
  onRemove,
  isOnlyRow,
}: FlashpointRowProps) {
  const handleFlashpointChange = useCallback((flashpointId: string) => {
    onUpdate(index, { ...selection, flashpointId });
  }, [index, selection, onUpdate]);

  const handleDifficultyChange = useCallback((difficulty: FlashpointDifficulty) => {
    onUpdate(index, { ...selection, difficulty });
    lastSelectedDifficulty = difficulty; // Remember for new rows
  }, [index, selection, onUpdate]);

  return (
    <div className="card card--xs card--bg-black card--bg-o-25 card--border card--border-blue-light d-flex align-items-center gap-sm">
      <div className="flashpoint-row-select">
        <select
          value={selection.flashpointId}
          onChange={(e) => handleFlashpointChange(e.target.value)}
          className="select"
        >
          <option value="">-- Select Flashpoint --</option>
          {/* A stored pick the catalogue no longer lists still shows, by id. */}
          {selection.flashpointId && !flashpoints.some((fp) => fp.id === selection.flashpointId) && (
            <option value={selection.flashpointId}>{selection.flashpointId}</option>
          )}
          {flashpoints.map((fp) => {
            const isDisabled = disabledFlashpointIds.has(fp.id) && fp.id !== selection.flashpointId;
            return (
              <option key={fp.id} value={fp.id} disabled={isDisabled}>
                {fp.name} {isDisabled ? '(used)' : ''}
              </option>
            );
          })}
        </select>
      </div>

      <div className="flashpoint-row-difficulty">
        <Segmented
          size="sm"
          aria-label="Difficulty"
          options={[
            { value: 'SM', label: 'SM' },
            { value: 'VM', label: 'VM' },
            { value: 'MM', label: 'MM' },
          ]}
          value={selection.difficulty}
          onChange={handleDifficultyChange}
        />
      </div>

      <button
        className="btn btn--ghost btn--icon btn--sm btn--red"
        onClick={() => onRemove(index)}
        title="Remove flashpoint"
        disabled={isOnlyRow && !selection.flashpointId}
      >
        ×
      </button>
    </div>
  );
});

export const FprRoundSettingsForm = memo(function FprRoundSettingsForm({
  players,
  onSubmit,
  onCancel,
  initialSettings,
  isEditing = false,
  allGameFlashpointSelections = [],
  teamCount = 0,
}: FprRoundSettingsFormProps) {
  // Initialize with at least one empty row for adding
  const [flashpointOptions, setFlashpointOptions] = useState<FlashpointSelection[]>(() => {
    if (initialSettings?.flashpointOptions && initialSettings.flashpointOptions.length > 0) {
      return initialSettings.flashpointOptions;
    }
    // Start with one empty row
    return [{ flashpointId: '', difficulty: lastSelectedDifficulty }];
  });
  const [hideProgressTimes, setHideProgressTimes] = useState<boolean>(
    initialSettings?.hideProgressTimes ?? false
  );
  const [includeBonusBosses, setIncludeBonusBosses] = useState<boolean>(
    initialSettings?.includeBonusBosses ?? false
  );
  const [isFinal, setIsFinal] = useState<boolean>(initialSettings?.isFinal ?? false);
  const [finalistCount, setFinalistCount] = useState<number>(
    initialSettings?.finalistCount ?? Math.max(1, Math.min(3, teamCount))
  );
  const [showFlashpointAlert, setShowFlashpointAlert] = useState(false);

  const handleSubmit = useCallback(() => {
    // Filter out empty selections
    const validSelections = flashpointOptions.filter(s => s.flashpointId);
    
    if (validSelections.length === 0) {
      setShowFlashpointAlert(true);
      return;
    }

    const settings: FprRoundSettings = {
      gameType: GameType.FP_RACE,
      flashpointOptions: validSelections,
      hideProgressTimes,
      includeBonusBosses,
      isFinal,
      ...(isFinal ? { finalistCount: Math.max(1, Math.min(finalistCount, teamCount || finalistCount)) } : {}),
      // selectedFlashpoint will be set when round starts
    };

    // All enabled players in the game participate in FPR rounds
    const roundPlayers: RoundPlayer[] = players.map((player) => ({
      playerId: player.playerId,
      socketId: player.socketId,
      characterName: player.characterName || '',
      status: 'active' as const,
    }));

    onSubmit(settings, roundPlayers);
  }, [flashpointOptions, hideProgressTimes, includeBonusBosses, isFinal, finalistCount, teamCount, players, onSubmit]);

  const handleUpdateRow = useCallback((index: number, selection: FlashpointSelection) => {
    setFlashpointOptions((prev) => {
      const updated = [...prev];
      updated[index] = selection;
      return updated;
    });
  }, []);

  const handleRemoveRow = useCallback((index: number) => {
    setFlashpointOptions((prev) => {
      if (prev.length <= 1) {
        // Keep at least one row, just clear it
        return [{ flashpointId: '', difficulty: lastSelectedDifficulty }];
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleAddRow = useCallback(() => {
    setFlashpointOptions((prev) => [
      ...prev,
      { flashpointId: '', difficulty: lastSelectedDifficulty },
    ]);
  }, []);

  const { flashpoints, loading: flashpointsLoading, error: flashpointsError } = useFlashpointCatalogue();

  // Build set of disabled flashpoint IDs (already used in this game or current form)
  const disabledFlashpointIds = new Set<string>([
    ...allGameFlashpointSelections.map(s => s.flashpointId),
    ...flashpointOptions.filter(s => s.flashpointId).map(s => s.flashpointId),
  ]);

  // Count valid selections
  const validSelectionCount = flashpointOptions.filter(s => s.flashpointId).length;

  if (players.length === 0) {
    return (
      <RoundFormBase
        playerCount={0}
        isEditing={isEditing}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        extraClassName="fpr-creation"
      >
        {null}
      </RoundFormBase>
    );
  }

  return (
    <RoundFormBase
      playerCount={players.length}
      isEditing={isEditing}
      submitDisabled={validSelectionCount === 0}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      extraClassName="fpr-creation"
    >
      {/* Help text */}
      <p className="field__hint">
        {validSelectionCount > 1 
          ? `When the round starts, one of these ${validSelectionCount} flashpoints will be randomly selected.`
          : validSelectionCount === 1
          ? 'This flashpoint will be used for the round.'
          : 'Select one or more flashpoints. If multiple are added, one will be randomly selected when the round starts.'}
      </p>

      {flashpointsError && (
        <p className="field__hint text-red">Couldn&apos;t load flashpoints from the platform: {flashpointsError}</p>
      )}
      {flashpointsLoading && <p className="field__hint">Loading flashpoints…</p>}

      {/* Flashpoint Rows */}
      <div className="d-flex flex-column gap-xs mb-base">
        <div className="d-flex gap-sm fs-xs fw-bold text-uppercase text-slate">
          <span className="header-flashpoint">Flashpoint</span>
          <span className="header-difficulty">Difficulty</span>
          <span className="header-actions"></span>
        </div>
        
        {flashpointOptions.map((selection, index) => (
          <FlashpointRow
            key={index}
            index={index}
            selection={selection}
            flashpoints={flashpoints}
            disabledFlashpointIds={disabledFlashpointIds}
            onUpdate={handleUpdateRow}
            onRemove={handleRemoveRow}
            isOnlyRow={flashpointOptions.length === 1}
          />
        ))}
      </div>

      {/* Add Row Button */}
      <button
        className="btn btn--outline btn--slate add-row-btn"
        onClick={handleAddRow}
        type="button"
      >
        + Add Another Flashpoint
      </button>

      {/* Include Bonus Bosses Toggle */}
      <div className="form-check mt-base">
        <input
          type="checkbox"
          id="include-bonus-bosses"
          className="form-check-input"
          checked={includeBonusBosses}
          onChange={(e) => setIncludeBonusBosses(e.target.checked)}
        />
        <label htmlFor="include-bonus-bosses" className="form-check-label">
          Include Bonus boss(es)?
        </label>
        <p className="field__hint fs-xs">
          When enabled, optional bonus bosses will be shown as blocks in the team overlay.
        </p>
      </div>

      {/* Hide Progress Times Toggle */}
      <div className="form-check mt-base">
        <input
          type="checkbox"
          id="hide-progress-times"
          className="form-check-input"
          checked={hideProgressTimes}
          onChange={(e) => setHideProgressTimes(e.target.checked)}
        />
        <label htmlFor="hide-progress-times" className="form-check-label">
          Hide boss kill times
        </label>
        <p className="field__hint fs-xs">
          When enabled, viewers see which bosses have been killed but not how long it took. Each team still sees their own times.
        </p>
      </div>

      {/* GRAND FINAL Toggle */}
      <div className="form-check mt-base">
        <input
          type="checkbox"
          id="is-final-round"
          className="form-check-input"
          checked={isFinal}
          onChange={(e) => setIsFinal(e.target.checked)}
        />
        <label htmlFor="is-final-round" className="form-check-label">
          🏆 Mark as GRAND FINAL round
        </label>
        <p className="field__hint fs-xs">
          When enabled, only the top N teams (by cumulative score) participate. Non-finalist teams&apos; kills are ignored, and no further rounds can be created after this one.
        </p>
        {isFinal && (
          <div className="mt-xs">
            <label htmlFor="finalist-count" className="form-check-label">
              Number of finalist teams
            </label>
            <input
              type="number"
              id="finalist-count"
              className="input"
              min={1}
              max={teamCount || undefined}
              value={finalistCount}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!Number.isNaN(v)) setFinalistCount(v);
              }}
            />
            <p className="field__hint fs-xs">
              {teamCount > 0
                ? `Top ${finalistCount} of ${teamCount} teams qualify. Ties at the cutoff are all included (e.g. if 3rd and 4th are tied, both compete).`
                : 'Top N teams qualify. Ties at the cutoff are all included.'}
            </p>
          </div>
        )}
      </div>

      {/* Actions */}
      <AlertModal
        isOpen={showFlashpointAlert}
        onClose={() => setShowFlashpointAlert(false)}
        title="Selection Required"
        message="Please select at least one flashpoint."
        variant="warning"
      />
    </RoundFormBase>
  );
});
